import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, Plane } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const Airports = () => {
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const filtered = airports.filter(a =>
      a.code.toLowerCase().includes(search.toLowerCase()) ||
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.city.toLowerCase().includes(search.toLowerCase())
    );

    const groups: Record<string, typeof airports> = {};
    filtered.forEach(airport => {
      const state = airport.city.split(",")[1]?.trim() || "Other";
      if (!groups[state]) groups[state] = [];
      groups[state].push(airport);
    });

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [search]);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "US Airport TSA Wait Times",
    "numberOfItems": airports.length,
    "itemListElement": airports.map((airport, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": `${airport.name} (${airport.code})`,
      "url": `https://airportbuddy.app/airports/${airport.code.toLowerCase()}`
    }))
  };

  return (
    <>
      <Helmet>
        <title>{"US Airport TSA Wait Times — " + airports.length + "+ Airports"}</title>
        <meta name="description" content={"Real-time TSA wait times for " + airports.length + "+ US airports. Check security lines and flight status before you leave."} />
        <meta name="keywords" content="all airports TSA wait times, US airport list, airport security wait times, browse airports, TSA times by airport" />
        <link rel="canonical" href="https://airportbuddy.app/airports" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://airportbuddy.app/airports" />
        <meta property="og:title" content={"US Airport TSA Wait Times — " + airports.length + "+ Airports"} />
        <meta property="og:description" content={"Browse live TSA security wait times for " + airports.length + "+ US airports. Pick your airport and see the line before you leave."} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={"US Airport TSA Wait Times — " + airports.length + "+ Airports"} />
        <meta name="twitter:description" content={"Browse live TSA security wait times for " + airports.length + "+ US airports."} />
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-4 min-h-[44px]">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Home
            </Link>
          </Button>

          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Airports", href: "/airports" }
          ]} />

          <div className="max-w-5xl mx-auto space-y-8">
            <header className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                All US Airport TSA Wait Times
              </h1>
              <p className="text-lg text-muted-foreground">
                Browse real-time TSA security wait times for {airports.length}+ major US airports
              </p>
            </header>

            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                type="search"
                placeholder="Search by airport code, name, or city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                aria-label="Search airports"
              />
            </div>

            {grouped.map(([state, stateAirports]) => (
              <section key={state}>
                <h2 className="text-xl font-semibold text-foreground mb-3">{state}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stateAirports.map(airport => (
                    <Link
                      key={airport.code}
                      to={`/airports/${airport.code.toLowerCase()}`}
                      className="group"
                    >
                      <Card className="hover:shadow-md hover:border-primary/40 transition-all duration-200 hover:-translate-y-0.5">
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="rounded-full w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Plane className="h-4 w-4 text-primary" aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                              {airport.name} ({airport.code})
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {airport.city}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            ))}

            {grouped.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No airports found matching "{search}"
              </p>
            )}

            <AppStoreCTA
              placement="airports_index"
              headline="Track your airports automatically"
              subline="The free Airport Buddy iPhone app saves your airports and alerts you when the line spikes."
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Airports;
